import { useState, useEffect } from 'react';
import {
  ArrowTrendingUpIcon,
  ArrowTrendingDownIcon,
  PlusIcon,
  ChartBarIcon,
  PencilIcon,
  TrashIcon
} from '@heroicons/react/24/outline';
import { supabase } from '../../../lib/supabase';
import { useTournament } from '../../TournamentContext';
import MobileTransactionSheet from '../../components/MobileTransactionSheet';
import MobileBudgetItemSheet from '../../components/MobileBudgetItemSheet';
import MobileBudgetAdjustmentSheet from '../../components/MobileBudgetAdjustmentSheet';
import DeleteConfirmationModal from '../../../components/DeleteConfirmationModal';
import { toast } from 'react-hot-toast';

function formatCurrency(value: number): string {
  if (value >= 1_000_000) return `₦${(value / 1_000_000).toFixed(1)}M`;
  if (value >= 1_000) return `₦${(value / 1_000).toFixed(0)}K`;
  return `₦${value.toLocaleString()}`;
}

interface Transaction {
  id: string;
  type: 'income' | 'expense';
  category: string;
  description: string;
  amount: number;
  transaction_date: string;
  date: string;
  status: 'pending' | 'completed' | 'cancelled';
  payment_method?: string;
  reference_number?: string;
  notes?: string;
}

interface BudgetItem {
  id: string;
  category: string;
  subcategory: string | null;
  description: string;
  allocated_amount: number;
  spent_amount: number;
  fiscal_year: number | null;
  status: string;
}

interface AdjustmentRecord {
  id: string;
  previous_amount: number;
  new_amount: number;
  adjustment_date: string;
  reason: string;
}

export default function MobileAdminFinance() {
  const { selectedTournament } = useTournament();
  const tournamentId = selectedTournament?.id || '';
  const [loading, setLoading] = useState(true);
  const [transactions, setTransactions] = useState<Transaction[]>([]);
  const [budgetItems, setBudgetItems] = useState<BudgetItem[]>([]);
  const [totalIncome, setTotalIncome] = useState(0);
  const [totalExpenses, setTotalExpenses] = useState(0);
  const [totalBudget, setTotalBudget] = useState(0);
  const [tab, setTab] = useState<'overview' | 'income' | 'expenses' | 'budget'>('overview');

  const [isTransactionModalOpen, setIsTransactionModalOpen] = useState(false);
  const [modalType, setModalType] = useState<'income' | 'expense'>('income');
  const [editingTransaction, setEditingTransaction] = useState<Transaction | null>(null);

  const [isBudgetItemOpen, setIsBudgetItemOpen] = useState(false);
  const [editingBudgetItem, setEditingBudgetItem] = useState<BudgetItem | null>(null);
  const [deletingBudgetItem, setDeletingBudgetItem] = useState<BudgetItem | null>(null);

  const [isAdjustmentOpen, setIsAdjustmentOpen] = useState(false);
  const [adjustingBudgetItem, setAdjustingBudgetItem] = useState<BudgetItem | null>(null);
  const [adjustmentHistory, setAdjustmentHistory] = useState<AdjustmentRecord[]>([]);

  useEffect(() => { if (tournamentId) fetchData(); }, [tournamentId]);

  const fetchData = async () => {
    try {
      setLoading(true);

      const { data: txData, error: txError } = await supabase
        .from('financial_transactions')
        .select('*')
        .eq('tournament_id', tournamentId)
        .order('transaction_date', { ascending: false });

      if (txError) throw txError;

      const txs = txData || [];
      setTransactions(txs);

      const income = txs.filter(t => t.type === 'income' && t.status === 'completed').reduce((s, t) => s + t.amount, 0);
      const expenses = txs.filter(t => t.type === 'expense' && t.status === 'completed').reduce((s, t) => s + t.amount, 0);
      setTotalIncome(income);
      setTotalExpenses(expenses);

      const { data: budgetData, error: budgetError } = await supabase
        .from('budget_items')
        .select('*')
        .eq('tournament_id', tournamentId)
        .order('category');
      if (budgetError) throw budgetError;
      const items = budgetData || [];
      setBudgetItems(items);
      setTotalBudget(items.reduce((s: number, b: BudgetItem) => s + (b.allocated_amount || 0), 0));
    } catch {
      toast.error('Failed to load financial data');
    } finally {
      setLoading(false);
    }
  };

  const openAddTransaction = (type: 'income' | 'expense') => {
    setModalType(type);
    setEditingTransaction(null);
    setIsTransactionModalOpen(true);
  };

  const openAdjustment = async (item: BudgetItem) => {
    setAdjustingBudgetItem(item);
    try {
      const { data } = await supabase
        .from('budget_adjustments')
        .select('*')
        .eq('budget_item_id', item.id)
        .order('adjustment_date', { ascending: false });
      setAdjustmentHistory(data || []);
    } catch {
      setAdjustmentHistory([]);
    }
    setIsAdjustmentOpen(true);
  };

  const handleDeleteBudgetItem = async () => {
    if (!deletingBudgetItem) return;
    try {
      const { error } = await supabase.from('budget_items').delete().eq('id', deletingBudgetItem.id);
      if (error) throw error;
      toast.success('Budget item deleted');
      setDeletingBudgetItem(null);
      fetchData();
    } catch {
      toast.error('Failed to delete budget item');
    }
  };

  const visibleTransactions = transactions.filter(t =>
    tab === 'overview' ? true :
    tab === 'income' ? t.type === 'income' :
    tab === 'expenses' ? t.type === 'expense' :
    false
  ).slice(0, 30);

  return (
    <div className="pb-4 space-y-4">
      {loading ? (
        <div className="flex justify-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-4 border-golf-orange border-t-transparent" />
        </div>
      ) : (
        <>
          {/* Summary strip */}
          <div className="px-4 pt-4">
            <div className="bg-white rounded-2xl border border-gray-100 shadow-sm divide-x divide-gray-100 grid grid-cols-3">
              <div className="px-3 py-4 text-center">
                <p className="text-[10px] text-gray-400 uppercase tracking-wide mb-1">Income</p>
                <p className="text-sm font-bold text-green-600 leading-tight">{formatCurrency(totalIncome)}</p>
              </div>
              <div className="px-3 py-4 text-center">
                <p className="text-[10px] text-gray-400 uppercase tracking-wide mb-1">Expenses</p>
                <p className="text-sm font-bold text-red-500 leading-tight">{formatCurrency(totalExpenses)}</p>
              </div>
              <div className="px-3 py-4 text-center">
                <p className="text-[10px] text-gray-400 uppercase tracking-wide mb-1">Net</p>
                <p className={`text-sm font-bold leading-tight ${totalIncome - totalExpenses >= 0 ? 'text-green-600' : 'text-red-500'}`}>
                  {formatCurrency(totalIncome - totalExpenses)}
                </p>
              </div>
            </div>
            {totalBudget > 0 && (
              <p className="text-xs text-gray-400 text-center mt-2">
                {formatCurrency(totalBudget - totalExpenses)} of {formatCurrency(totalBudget)} budget remaining
              </p>
            )}
          </div>

          {/* Quick add buttons */}
          <div className="px-4 grid grid-cols-2 gap-2">
            <button
              onClick={() => openAddTransaction('income')}
              className="bg-green-500 text-white rounded-xl py-2.5 flex items-center justify-center gap-2 text-sm font-medium"
            >
              <PlusIcon className="w-4 h-4" />
              Add Income
            </button>
            <button
              onClick={() => openAddTransaction('expense')}
              className="bg-red-500 text-white rounded-xl py-2.5 flex items-center justify-center gap-2 text-sm font-medium"
            >
              <PlusIcon className="w-4 h-4" />
              Add Expense
            </button>
          </div>

          {/* Sticky Tab filter */}
          <div className="sticky top-14 z-10 bg-gray-50 px-4 pt-2 pb-3 border-b border-gray-100">
            <div className="flex gap-1 bg-gray-200/60 p-1 rounded-xl">
              {(['overview', 'income', 'expenses', 'budget'] as const).map(t => (
                <button
                  key={t}
                  onClick={() => setTab(t)}
                  className={`flex-1 py-2 text-xs font-medium rounded-lg transition-colors capitalize ${
                    tab === t ? 'bg-white text-golf-gray shadow-sm' : 'text-gray-500'
                  }`}
                >
                  {t}
                </button>
              ))}
            </div>
          </div>

          {/* Budget Items Tab */}
          {tab === 'budget' ? (
            <div className="px-4 space-y-3">
              <div className="flex items-center justify-between">
                <p className="text-xs text-gray-500">{budgetItems.length} item{budgetItems.length !== 1 ? 's' : ''}</p>
                <button
                  onClick={() => { setEditingBudgetItem(null); setIsBudgetItemOpen(true); }}
                  className="bg-golf-orange text-white rounded-xl px-3 py-2 flex items-center gap-1.5 text-sm font-medium"
                >
                  <PlusIcon className="w-4 h-4" />
                  Add Item
                </button>
              </div>
              {budgetItems.length === 0 ? (
                <div className="text-center py-12 text-gray-400 text-sm">No budget items yet</div>
              ) : (
                budgetItems.map(item => {
                  const pct = item.allocated_amount > 0 ? Math.min((item.spent_amount / item.allocated_amount) * 100, 100) : 0;
                  return (
                    <div key={item.id} className="bg-white rounded-2xl border border-gray-100 shadow-sm p-4">
                      <div className="flex items-start justify-between gap-2 mb-3">
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-semibold text-golf-gray truncate">{item.description}</p>
                          <p className="text-xs text-gray-500 capitalize">{item.category}{item.subcategory ? ` · ${item.subcategory}` : ''}</p>
                        </div>
                        <div className="flex items-center gap-1 flex-shrink-0">
                          <button
                            onClick={() => openAdjustment(item)}
                            className="p-1.5 rounded-lg text-blue-500 hover:bg-blue-50 transition-colors"
                          >
                            <ChartBarIcon className="w-3.5 h-3.5" />
                          </button>
                          <button
                            onClick={() => { setEditingBudgetItem(item); setIsBudgetItemOpen(true); }}
                            className="p-1.5 rounded-lg text-gray-400 hover:text-golf-orange hover:bg-golf-orange/5 transition-colors"
                          >
                            <PencilIcon className="w-3.5 h-3.5" />
                          </button>
                          <button
                            onClick={() => setDeletingBudgetItem(item)}
                            className="p-1.5 rounded-lg text-gray-400 hover:text-red-500 hover:bg-red-50 transition-colors"
                          >
                            <TrashIcon className="w-3.5 h-3.5" />
                          </button>
                        </div>
                      </div>
                      <div className="flex justify-between text-xs text-gray-500 mb-2">
                        <span>Spent: {formatCurrency(item.spent_amount)}</span>
                        <span>Allocated: {formatCurrency(item.allocated_amount)}</span>
                      </div>
                      <div className="h-1.5 bg-gray-100 rounded-full overflow-hidden">
                        <div
                          className={`h-full rounded-full ${pct >= 90 ? 'bg-red-500' : pct >= 70 ? 'bg-amber-500' : 'bg-green-500'}`}
                          style={{ width: `${pct}%` }}
                        />
                      </div>
                      <p className="text-xs text-gray-400 mt-1 text-right">{pct.toFixed(0)}% used</p>
                    </div>
                  );
                })
              )}
            </div>
          ) : (
            /* Transactions */
            visibleTransactions.length === 0 ? (
              <div className="px-4 text-center py-12 text-gray-400 text-sm">No transactions yet</div>
            ) : (
              <div className="px-4 space-y-2">
                {visibleTransactions.map(tx => (
                  <button
                    key={tx.id}
                    onClick={() => { setEditingTransaction(tx); setModalType(tx.type); setIsTransactionModalOpen(true); }}
                    className="w-full bg-white rounded-2xl border border-gray-100 shadow-sm p-4 text-left active:bg-gray-50 transition-colors"
                  >
                    <div className="flex items-center gap-3">
                      <div className={`w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0 ${tx.type === 'income' ? 'bg-green-50' : 'bg-red-50'}`}>
                        {tx.type === 'income'
                          ? <ArrowTrendingUpIcon className="w-5 h-5 text-green-600" />
                          : <ArrowTrendingDownIcon className="w-5 h-5 text-red-500" />
                        }
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="font-medium text-golf-gray text-sm truncate">{tx.description || tx.category}</p>
                        <p className="text-xs text-gray-500">{tx.category} · {new Date(tx.transaction_date || tx.date).toLocaleDateString()}</p>
                      </div>
                      <p className={`text-sm font-bold flex-shrink-0 ${tx.type === 'income' ? 'text-green-600' : 'text-red-500'}`}>
                        {tx.type === 'income' ? '+' : '-'}{formatCurrency(tx.amount)}
                      </p>
                    </div>
                  </button>
                ))}
              </div>
            )
          )}
        </>
      )}

      {tournamentId && isTransactionModalOpen && (
        <MobileTransactionSheet
          isOpen={isTransactionModalOpen}
          onClose={() => { setIsTransactionModalOpen(false); setEditingTransaction(null); fetchData(); }}
          tournamentId={tournamentId}
          transactionType={modalType}
          transaction={editingTransaction}
        />
      )}

      {tournamentId && (
        <MobileBudgetItemSheet
          isOpen={isBudgetItemOpen}
          onClose={() => { setIsBudgetItemOpen(false); setEditingBudgetItem(null); }}
          budgetItem={editingBudgetItem}
          tournamentId={tournamentId}
          onSuccess={fetchData}
        />
      )}

      <MobileBudgetAdjustmentSheet
        isOpen={isAdjustmentOpen}
        onClose={() => { setIsAdjustmentOpen(false); setAdjustingBudgetItem(null); fetchData(); }}
        budgetItem={adjustingBudgetItem}
        adjustmentHistory={adjustmentHistory}
        onSuccess={fetchData}
      />

      <DeleteConfirmationModal
        isOpen={!!deletingBudgetItem}
        onClose={() => setDeletingBudgetItem(null)}
        onConfirm={handleDeleteBudgetItem}
        title="Delete Budget Item"
        message={`Are you sure you want to delete "${deletingBudgetItem?.description}"? This cannot be undone.`}
      />
    </div>
  );
}
